import { WebSocketEventMap } from '../types/websocket';

class WebSocketService {
  private static instance: WebSocketService;
  private socket: WebSocket | null = null;
  private eventHandlers: Map<string, Set<Function>> = new Map();

  private constructor() {
    this.setupConnection();
  }

  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  private setupConnection(): void {
    // Eğer token yoksa bağlanma
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const wsUrl = `${import.meta.env.VITE_WS_URL}/admin?token=${token}`;
      this.socket = new WebSocket(wsUrl);
      
      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
    } catch (error) {
      console.error('WebSocket bağlantısı oluşturulamadı:', error);
    }
  }

  private handleOpen(): void {
    console.log('✅ WebSocket bağlantısı kuruldu');
    // Bağlantı açıldığında ilgili event'i tetikle
    this.triggerEvent('open');
  }

  private handleClose(event: CloseEvent): void {
    console.log('👋 WebSocket bağlantısı kapandı', event);
    this.socket = null;
    this.triggerEvent('close', event);
    
    // Bir süre sonra tekrar bağlan
    setTimeout(() => this.setupConnection(), 5000);
  }

  private handleError(event: Event): void {
    console.error('❌ WebSocket hatası:', event);
    this.triggerEvent('error', event);
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      console.log('📥 WebSocket mesajı alındı:', data);
      
      // Mesaj tipine göre event'i tetikle
      if (data.type) {
        this.triggerEvent(data.type, data);
      }
      
      // Genel message event'i de tetikle
      this.triggerEvent('message', data);
    } catch (error) {
      console.error('WebSocket mesajı işlenemedi:', error);
    }
  }

  public on<K extends keyof WebSocketEventMap>(event: K, callback: (data: WebSocketEventMap[K]) => void): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    
    const handlers = this.eventHandlers.get(event)!;
    handlers.add(callback);
    
    // Unsubscribe fonksiyonu
    return () => {
      if (this.eventHandlers.has(event)) {
        const handlers = this.eventHandlers.get(event)!;
        handlers.delete(callback);
      }
    };
  }

  private triggerEvent(event: string, data?: any): void {
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event)!;
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Event handler (${event}) hatası:`, error);
        }
      });
    }
  }

  public send(data: any): boolean {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify(data));
        return true;
      } catch (error) {
        console.error('WebSocket mesajı gönderilemedi:', error);
        return false;
      }
    }
    return false;
  }

  public getSocket(): WebSocket | null {
    return this.socket;
  }

  public isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  public reconnect(): void {
    if (this.socket) {
      this.socket.close();
    }
    this.setupConnection();
  }
}

const websocketService = WebSocketService.getInstance();
export default websocketService;
